import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useUnsavedChanges } from "./UnsavedChangesContext";

export default function TopNav({ onLogout }) {
  const { requestConfirm } = useUnsavedChanges();
  const navigate = useNavigate();
  const location = useLocation();
  function handleNavClick(event, to) {
    event.preventDefault();
    if (location.pathname === to) return;
    requestConfirm(() => navigate(to));
  }
  const [theme, setTheme] = useState("system");

  useEffect(() => {
    const stored = localStorage.getItem("brushcreek_theme");
    const normalized = stored === "default" || stored === "white" ? "light" : stored;
    const next = normalized || "system";
    setTheme(next);
    const apply = (value) => {
      if (value === "light") {
        document.documentElement.removeAttribute("data-theme");
        return;
      }
      document.documentElement.setAttribute("data-theme", value);
    };

    const applySystem = () => {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      apply(prefersDark ? "dark" : "light");
    };

    if (next === "system") {
      applySystem();
      const media = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => applySystem();
      if (media.addEventListener) {
        media.addEventListener("change", handler);
      } else {
        media.addListener(handler);
      }
      return () => {
        if (media.removeEventListener) {
          media.removeEventListener("change", handler);
        } else {
          media.removeListener(handler);
        }
      };
    }

    apply(next);
  }, []);

  function handleThemeChange(event) {
    const next = event.target.value;
    setTheme(next);
    localStorage.setItem("brushcreek_theme", next);
    if (next === "light") {
      document.documentElement.removeAttribute("data-theme");
      return;
    }
    if (next === "system") {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      document.documentElement.setAttribute("data-theme", prefersDark ? "dark" : "light");
      return;
    }
    document.documentElement.setAttribute("data-theme", next);
  }

  const linkStyle = ({ isActive }) => ({
    padding: "10px 16px",
    textDecoration: "none",
    borderBottom: isActive ? "3px solid var(--nav-link-active)" : "3px solid transparent",
    fontWeight: isActive ? 700 : 500,
    color: "var(--nav-link)",
  });

  return (
    <div
      className="top-nav"
      style={{
        display: "flex",
        gap: 16,
        borderBottom: "1px solid var(--nav-border)",
        marginBottom: 20,
        alignItems: "center",
      }}
    >
      <div style={{ display: "flex", gap: 16 }}>
        <NavLink to="/" style={linkStyle} end onClick={(e) => handleNavClick(e, "/")}>
          Schedule
        </NavLink>
        <NavLink to="/guests" style={linkStyle} onClick={(e) => handleNavClick(e, "/guests")}>
          Guests
        </NavLink>
        <NavLink to="/activities" style={linkStyle} onClick={(e) => handleNavClick(e, "/activities")}>
          Activities
        </NavLink>
        <NavLink to="/guides" style={linkStyle} onClick={(e) => handleNavClick(e, "/guides")}>
          Guides
        </NavLink>
        <NavLink to="/families" style={linkStyle} onClick={(e) => handleNavClick(e, "/families")}>
          Families/Groups
        </NavLink>
        <NavLink to="/optimizer" style={linkStyle} onClick={(e) => handleNavClick(e, "/optimizer")}>
          Optimizer
        </NavLink>
        <NavLink to="/reports" style={linkStyle} onClick={(e) => handleNavClick(e, "/reports")}>
          Reports
        </NavLink>
      </div>

      <div style={{ marginLeft: "auto", display: "flex", gap: 12, alignItems: "center" }}>
        <fieldset id="theme-switcher" aria-label="Theme">

          <label htmlFor="theme-system" title="System default theme">
            <input
              type="radio"
              name="color-theme"
              value="system"
              id="theme-system"
              checked={theme === "system"}
              onChange={handleThemeChange}
            />
            <svg
              id="icon-system"
              fill="currentColor"
              width="1em"
              height="1em"
              viewBox="0 0 32 32"
              role="img"
              aria-hidden="true"
            >
              <title>System default theme</title>
              <path d="M6 6h20v14H6zM10 24h12v2H10zM14 20h4v4h-4z"></path>
            </svg>
          </label>

          <label htmlFor="theme-dark" title="Dark theme">
            <input
              type="radio"
              name="color-theme"
              value="dark"
              id="theme-dark"
              checked={theme === "dark"}
              onChange={handleThemeChange}
            />
            <svg
              id="icon-moon"
              stroke="currentColor"
              width="1em"
              height="1em"
              viewBox="0 0 16 16"
              role="img"
              aria-hidden="true"
              fill="none"
            >
              <title>Dark theme</title>
              <path d="M10.5 1.5A6 6 0 1 0 14.5 11 6.5 6.5 0 0 1 10.5 1.5z"></path>
            </svg>
          </label>

          <label htmlFor="theme-light" title="Light theme">
            <input
              type="radio"
              name="color-theme"
              value="light"
              id="theme-light"
              checked={theme === "light"}
              onChange={handleThemeChange}
            />
            <svg
              id="icon-sun"
              stroke="currentColor"
              width="1em"
              height="1em"
              viewBox="0 0 16 16"
              role="img"
              aria-hidden="true"
              fill="none"
            >
              <title>Light theme</title>
              <circle cx="8" cy="8" r="3.25"></circle>
              <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.1 3.1l1.4 1.4M11.5 11.5l1.4 1.4M12.9 3.1l-1.4 1.4M4.5 11.5l-1.4 1.4"></path>
            </svg>
          </label>
        </fieldset>
        <span style={{ fontWeight: 700 }}>Admin enabled</span>
        <button onClick={onLogout} style={{ fontSize: 12 }}>
          Log out
        </button>
      </div>
    </div>
  );
}
